// src/modules/app-views/collaborators/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Table } from 'antd';
import { useListContext } from 'components/core/ListProvider';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import {
  cpfCnpjColumn,
  nameColumn,
  fullNameColumn,
  cityColumn,
  contactsColumn,
  nameWithAvatarColumn,
  fullNameWithAvatarColumn,
} from 'modules/app-views/companies/pages/Init/components/tableColumns';
import BackToHomeLink from 'components/core/BackToHomeLink';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CollaboratorsInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    data,
    loading,
    tableKey,
    pagination,
    handleTableChanges,
    rowSelection,
    buildTableColumns,
    availableTableColumns,
    buildNumberLineColumn,
    buildDocumentCodeColumn,
    buildCreatedAtColumn,
    buildUpdatedAtColumn,
    buildDocumentInfoIconsColumn,
    buildActionsButtonColumn,
    buildColumnProps,
    query,
    tableComponents,
    hiddenColumns,
    isEditingTable,
    TableFeatures,
    folder,
    renderArchiveRecycleActions,
  } = useListContext();

  const storedListSettings = useZustand('module', 'listSettings');
  const storedLanguage = useZustand('theme', 'language');

  // Serialize sort and search for stable dependencies (avoids infinite loops from function recreation)
  const sortKey = JSON.stringify(query?.sort || []);
  const searchKey = JSON.stringify(query?.search || {});

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Rebuild columns when sort or settings change
  useEffect(() => {
    buildTableColumns?.([
      buildNumberLineColumn(),

      buildDocumentCodeColumn(),

      cpfCnpjColumn(buildColumnProps),

      nameColumn(buildColumnProps, { title: 'collaborator' }),

      fullNameColumn(buildColumnProps, { title: 'full_name' }),

      nameWithAvatarColumn(buildColumnProps, { title: 'collaborator', subtitleField: 'cpf', avatarShape: 'circle' }),

      fullNameWithAvatarColumn(buildColumnProps, { title: 'collaborator', subtitleField: 'cpf', avatarShape: 'circle' }),

      cityColumn(buildColumnProps),

      contactsColumn(buildColumnProps),

      buildCreatedAtColumn(),

      buildUpdatedAtColumn(),

      buildDocumentInfoIconsColumn(),

      buildActionsButtonColumn({
        view: false,
        ...(['archive', 'recycle'].includes(folder)
          ? // ? { customRender: renderArchiveRecycleActions }
            { fitInDropDownMenu: ['edit', 'remove'] }
          : { fitInDropDownMenu: ['archive', 'remove'] }),
      }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedListSettings?.showNumberOfLine,
    sortKey,
    searchKey,
    storedLanguage,
    hiddenColumns,
    isEditingTable,
    query?.drawer?.folder,
    folder,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <TableFeatures />

      <Table
        key={tableKey}
        scroll={{ x: 'max-content' }}
        bordered
        rowKey="_id"
        size={isMobile ? 'small' : 'large'}
        columns={availableTableColumns}
        dataSource={data || []}
        pagination={pagination}
        className="with-checkbox"
        loading={loading}
        onChange={handleTableChanges}
        rowSelection={rowSelection}
        components={tableComponents}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CollaboratorsInit;
